import type { ConfigType } from '@plone/registry';

type Fieldset = { id: string; fields: string[] };
type Schema = { fieldsets?: Fieldset[] };
type SchemaEnhancerArgs = { schema: Schema; [key: string]: unknown };
type SchemaEnhancer = (args: SchemaEnhancerArgs) => Schema;
type Variation = { id: string; schemaEnhancer?: SchemaEnhancer };

const TABS_BLOCK = 'tabs_block';
const DEFAULT_VARIATION = 'default';
const MENU_FIELDSET = 'menu';

/** Do menu das abas, o editor só decide posição e alinhamento. */
const MENU_FIELDS_MANTIDOS = ['menuPosition', 'menuAlign'];

/**
 * As variações de carrossel do `@eeacms/volto-tabs-block` arrastam o CSS do
 * `slick-carousel` (13,5 KB no bundle de produção, medido em 07/09/2026) para
 * dentro do bundle que todo visitante anônimo baixa. Varredura dos 3.812
 * Documents do site não achou um único bloco de abas usando carrossel: os 67
 * existentes estão todos na variação `default`.
 *
 * Tirar as variações do registro anda junto com o alias que neutraliza o CSS
 * em `razzle.extend.js` — sem isso o editor ofereceria uma variação que
 * renderiza sem estilo.
 */
const VARIACOES_REMOVIDAS = ['carousel-horizontal', 'carousel-vertical'];

const trimMenuFields = (schema: Schema) => {
  const menu = schema?.fieldsets?.find(
    (fieldset) => fieldset.id === MENU_FIELDSET,
  );
  if (menu) {
    menu.fields = menu.fields.filter((field) =>
      MENU_FIELDS_MANTIDOS.includes(field),
    );
  }
  return schema;
};

const getDefaultVariation = (config: ConfigType): Variation | undefined => {
  const blocksConfig = config.blocks.blocksConfig as Record<string, any>;
  return blocksConfig[TABS_BLOCK]?.variations?.find(
    (variation: Variation) => variation.id === DEFAULT_VARIATION,
  );
};

const dropCarouselVariations = (config: ConfigType) => {
  const blocksConfig = config.blocks.blocksConfig as Record<string, any>;
  const block = blocksConfig[TABS_BLOCK];
  if (!Array.isArray(block?.variations)) return;

  block.variations = block.variations.filter(
    (variation: Variation) => !VARIACOES_REMOVIDAS.includes(variation.id),
  );
};

/**
 * Enxuga a barra lateral da variação padrão do bloco de abas
 * (`@eeacms/volto-tabs-block`), que chega com um punhado de opções de menu
 * fora do design, e tira do registro as variações de carrossel, que não são
 * usadas e só engordam o bundle. O `schemaEnhancer` que já estiver registrado
 * é preservado: enxugamos o resultado dele, não o substituímos.
 */
export const updateTabsConfig = (config: ConfigType) => {
  dropCarouselVariations(config);

  const variation = getDefaultVariation(config);
  if (!variation) return config;

  const base = variation.schemaEnhancer;
  variation.schemaEnhancer = (args: SchemaEnhancerArgs) =>
    trimMenuFields(base ? base(args) : args.schema);

  return config;
};

export default updateTabsConfig;
